<script lang="ts">
  import { Consultorios, type Profesional } from '../lib/api';

  let profesionales = $state<Profesional[]>([]);
  let error = $state<string|null>(null);
  let cargando = $state(false);

  let nuevoProf = $state<Profesional>({ nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true });
  let editandoId = $state<string | null>(null);

  async function recargar() {
    cargando = true; error = null;
    try { profesionales = await Consultorios.profesionales(true); }
    catch (e: any) { error = e.message; }
    cargando = false;
  }

  function editar(p: Profesional) {
    editandoId = p.id ?? null;
    nuevoProf = {
      nombre: p.nombre,
      colegiado: p.colegiado ?? '',
      nit: p.nit ?? '',
      tipoRelacion: p.tipoRelacion,
      honorarioPct: p.honorarioPct ?? 100,
      activo: p.activo ?? true,
    };
  }

  function cancelarEdicion() {
    editandoId = null;
    nuevoProf = { nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true };
  }

  async function guardarProfesional() {
    error = null;
    if (!nuevoProf.nombre) { error = 'Nombre del profesional requerido'; return; }
    try {
      if (editandoId) {
        await Consultorios.actualizarProf(editandoId, { ...nuevoProf });
      } else {
        await Consultorios.crearProf({ ...nuevoProf });
      }
      nuevoProf = { nombre:'', colegiado:'', nit:'', tipoRelacion:'EMPLEADO', honorarioPct:100, activo:true };
      editandoId = null;
      await recargar();
    } catch (e: any) { error = e.message; }
  }

  async function eliminarProfesional(p: Profesional) {
    if (!confirm(`¿Eliminar a ${p.nombre}?`)) return;
    try { await Consultorios.eliminarProf(p.id!); await recargar(); }
    catch (e: any) { error = e.message; }
  }

  $effect(() => { recargar(); });
</script>

<section class="page">
  <h2>Profesionales</h2>
  <p class="sub">Gestione los médicos y profesionales que atienden en su hospital.</p>

  {#if error}<div class="err">{error}</div>{/if}

  <div class="card">
    <h3>{editandoId ? 'Editar profesional' : 'Nuevo profesional'}</h3>
    <div class="form">
      <label class="field"><span>Nombre completo</span><input placeholder="Nombre completo" bind:value={nuevoProf.nombre}/></label>
      <label class="field"><span>Colegiado</span><input placeholder="Colegiado" bind:value={nuevoProf.colegiado}/></label>
      <label class="field"><span>NIT</span><input placeholder="NIT" bind:value={nuevoProf.nit}/></label>
      <div class="row">
        <label class="field"><span>Relación</span>
          <select bind:value={nuevoProf.tipoRelacion}>
            <option value="EMPLEADO">Empleado</option>
            <option value="INDEPENDIENTE">Independiente</option>
          </select>
        </label>
        <label class="field"><span>% honorario</span><input type="number" min="0" max="100" step="0.01" placeholder="% honorario" bind:value={nuevoProf.honorarioPct}/></label>
      </div>
      <button onclick={guardarProfesional}>{editandoId ? 'Guardar cambios' : 'Crear profesional'}</button>
      {#if editandoId}
        <button type="button" class="ghost" onclick={cancelarEdicion}>Cancelar</button>
      {/if}
    </div>
  </div>

  <div class="card" style="margin-top:16px">
    <h3>Lista de profesionales</h3>
    <table>
      <thead><tr><th>Nombre</th><th>Colegiado</th><th>NIT</th><th>Relación</th><th>% honorario</th><th></th></tr></thead>
      <tbody>
        {#each profesionales as p (p.id)}
          <tr>
            <td>{p.nombre}</td>
            <td>{p.colegiado ?? '—'}</td>
            <td>{p.nit ?? '—'}</td>
            <td>{p.tipoRelacion}</td>
            <td>{p.honorarioPct ?? 100}%</td>
            <td>
              <button onclick={() => editar(p)}>Editar</button>
              <button onclick={() => eliminarProfesional(p)}>Eliminar</button>
            </td>
          </tr>
        {/each}
        {#if profesionales.length === 0 && !cargando}<tr><td colspan="6">No hay profesionales registrados.</td></tr>{/if}
      </tbody>
    </table>
  </div>
</section>

<style>
  .page { padding: 16px; max-width: 720px; }
  h2 { margin: 0 0 4px; font-size: 1.4rem; color: #111827; }
  .sub { color: #6b7280; font-size: 13px; margin: 0 0 20px; }
  .err { background: #fee2e2; color: #dc2626; padding: 10px 14px; border-radius: 8px; margin-bottom: 12px; font-size: 13px; }

  .card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 24px; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .card h3 { font-size: 16px; margin: 0 0 12px; color: #166534; }

  .form { display: flex; gap: 8px; flex-wrap: wrap; align-items: end; }
  .field { display: flex; flex-direction: column; gap: .3rem; flex: 1; min-width: 120px; }
  .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form input, .form select {
    padding: 8px 12px; border: 1px solid #e5e7eb; border-radius: 8px;
    font: inherit; font-size: 13px; background: #fff; color: #111827;
  }
  .form input:focus, .form select:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  .form button {
    padding: 8px 16px; background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0; border-radius: 8px;
    font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; white-space: nowrap; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s;
  }
  .form button:hover { transform: translateY(-1px); }
  .form button.ghost { background: #fff; color: #475569; border: 1px solid #e5e7eb; box-shadow: none; }
  .form button.ghost:hover { background: #f1f5f9; transform: none; }
  .row { display: flex; gap: 8px; flex: 1; min-width: 200px; }

  table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12.5px; }
  th { text-align: left; padding: 8px 10px; color: #6b7280; font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; background: #f0fdf4; }
  td { padding: 8px 10px; border-top: 1px solid #f3f4f6; color: #111827; }
  td button {
    padding: 4px 10px; background: none; border: 1px solid #e5e7eb; border-radius: 6px;
    font: inherit; font-size: 11px; font-weight: 600; cursor: pointer; color: #16a34a;
  }
  td button:hover { background: #f0fdf4; }
</style>
